import { Link } from 'react-router-dom';
import MarketingLayout from '../components/MarketingLayout';
import layoutStyles from '../components/MarketingLayout.module.css';
import styles from './LandingPage.module.css';

const HOW_IT_WORKS = [
  {
    step: '01',
    title: 'Connect calendars',
    body: 'Google Calendar, Microsoft 365. OAuth consent flow. Bidirectional sync.',
  },
  {
    step: '02',
    title: 'Query availability',
    body: 'Real availability across any connected calendar. Multiple people, multiple time zones.',
  },
  {
    step: '03',
    title: 'Propose and book',
    body: 'Propose the right time. Get consent. Confirm the booking.',
  },
];

const DIFFERENTIATORS = [
  {
    label: 'Demand-side coordination',
    body: 'Propose on behalf of anyone, with their consent.',
  },
  {
    label: 'Bidirectional sync',
    body: 'Read and write across Google, Microsoft, Salesforce, HubSpot.',
  },
  {
    label: 'Developer-first',
    body: 'Full OpenAPI 3.0 spec, SDKs, and clear documentation.',
  },
  {
    label: 'Consent-first',
    body: 'Every calendar access is explicitly granted and revocable.',
  },
  {
    label: 'Infrastructure, not product',
    body: 'Invisible to end users, like Stripe at checkout.',
  },
];

export default function LandingPage() {
  return (
    <MarketingLayout>

        {/* ── Hero ── */}
        <section className={styles.hero}>
          <div className={layoutStyles.sectionInner}>
            <div className="section-label">Infrastructure</div>
            <h1 className={styles.heroHeadline}>
              Infrastructure for <em className={styles.heroEm}>Time</em>
            </h1>
            <p className={styles.heroSub}>
              A scheduling API for developers and businesses. Vennio integrates with Google Calendar and Microsoft 365 to check availability, propose times, and create events — with explicit user consent.
            </p>
            <Link to="/auth" className={`btn btn-primary ${styles.heroCta}`}>
              Get Started
            </Link>
          </div>
        </section>

        {/* ── About Vennio ── */}
        <section className={layoutStyles.section}>
          <div className={layoutStyles.sectionInner}>
            <div className="section-label">About Vennio</div>
            <div className={styles.whatGrid}>
              <div>
                <h2 className={styles.sectionHeadline}>
                  What this app does.
                </h2>
              </div>
              <div className={styles.whatBody}>
                <p>
                  Vennio is scheduling infrastructure used by developers and businesses to add calendar functionality to their applications. After a user grants access through Google's standard OAuth consent screen, Vennio can read their calendar availability and create new events on their behalf. Vennio requests only the minimum scopes needed for these functions — <code>calendar.events</code> and <code>calendar.calendarlist.readonly</code> — and users can revoke access at any time from their <a href="https://myaccount.google.com/permissions">Google Account permissions page</a>.
                </p>
                <p>
                  Vennio does not sell user data, does not use calendar data for advertising, and does not share data with third parties beyond what is required to deliver the requested scheduling function.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* ── What Vennio Is ── */}
        <section className={layoutStyles.section}>
          <div className={layoutStyles.sectionInner}>
            <div className="section-label">What it is</div>
            <div className={styles.whatGrid}>
              <div>
                <h2 className={styles.sectionHeadline}>
                  Scheduling infrastructure for developers.
                </h2>
              </div>
              <div className={styles.whatBody}>
                <p>
                  A single API that reads, writes, and coordinates across every calendar — Google, Microsoft, Salesforce, HubSpot.
                </p>
                <p className={styles.analogyLine}>
                  Stripe abstracted payments. Vennio abstracts time.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* ── How It Works ── */}
        <section className={`${layoutStyles.section} ${layoutStyles.sectionWarm}`}>
          <div className={layoutStyles.sectionInner}>
            <div className="section-label">How it works</div>
            <div className={styles.stepsGrid}>
              {HOW_IT_WORKS.map(({ step, title, body }) => (
                <div key={step} className={styles.stepCard}>
                  <span className={styles.stepNum}>{step}</span>
                  <h3 className={styles.stepTitle}>{title}</h3>
                  <p className={styles.stepBody}>{body}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── Differentiators ── */}
        <section className={layoutStyles.section}>
          <div className={layoutStyles.sectionInner}>
            <div className="section-label">Why Vennio</div>
            <div className={styles.diffList}>
              {DIFFERENTIATORS.map(({ label, body }) => (
                <div key={label} className={styles.diffItem}>
                  <span className={styles.diffLabel}>{label}</span>
                  <span className={styles.diffBody}>{body}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── CTA Band ── */}
        <section className={styles.ctaBand}>
          <div className={layoutStyles.sectionInner}>
            <p className={styles.ctaEyebrow}>Free tier — 1,000 bookings/month, no credit card required</p>
            <h2 className={styles.ctaHeadline}>Ready to build on time?</h2>
            <Link to="/auth" className={`btn btn-primary ${styles.ctaBtn}`}>
              Get Started
            </Link>
          </div>
        </section>

    </MarketingLayout>
  );
}
